const ACCESS_TOKEN_KEY = 'pacilo.admin.access_token';
const REFRESH_TOKEN_KEY = 'pacilo.admin.refresh_token';
const EXPIRES_AT_KEY = 'pacilo.admin.expires_at';
const REFRESH_EXPIRES_AT_KEY = 'pacilo.admin.refresh_expires_at';
const LEGACY_TOKEN_KEY = ACCESS_TOKEN_KEY;

const ADMIN_SESSION_KEYS = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  EXPIRES_AT_KEY,
  REFRESH_EXPIRES_AT_KEY,
];

const DEFAULT_REFRESH_LEAD_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;

function getStorage() {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function readStorageItem(storage, key) {
  if (!storage) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(storage, key, value) {
  if (!storage) {
    throw new Error('Admin session storage is unavailable.');
  }
  try {
    storage.setItem(key, value);
  } catch (error) {
    throw new Error(`Failed to persist admin session key: ${key}`, { cause: error });
  }
  const readBack = readStorageItem(storage, key);
  if (readBack !== value) {
    throw new Error(`Failed to verify admin session key: ${key}`);
  }
}

function removeStorageItem(storage, key) {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage access failures during teardown.
  }
}

function migrateLegacySessionToken(storage) {
  if (!storage) {
    return null;
  }
  const existing = readStorageItem(storage, ACCESS_TOKEN_KEY);
  if (existing) {
    return existing;
  }
  try {
    const legacy = sessionStorage.getItem(LEGACY_TOKEN_KEY);
    if (!legacy) {
      return null;
    }
    writeStorageItem(storage, ACCESS_TOKEN_KEY, legacy);
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    return legacy;
  } catch {
    return null;
  }
}

function persistExpiry(storage, key, expiresIn) {
  const parsedExpiresIn = Number(expiresIn);
  if (Number.isFinite(parsedExpiresIn) && parsedExpiresIn > 0) {
    writeStorageItem(storage, key, String(Date.now() + parsedExpiresIn * 1000));
    return;
  }
  removeStorageItem(storage, key);
}

export function getAccessToken() {
  const storage = getStorage();
  const token = migrateLegacySessionToken(storage);
  if (token) {
    return token;
  }
  try {
    return sessionStorage.getItem(LEGACY_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getRefreshToken() {
  return readStorageItem(getStorage(), REFRESH_TOKEN_KEY);
}

export function getTokenExpiresAt() {
  const storage = getStorage();
  const raw = readStorageItem(storage, EXPIRES_AT_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function getRefreshExpiresAt() {
  const storage = getStorage();
  const raw = readStorageItem(storage, REFRESH_EXPIRES_AT_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function hasStoredAdminSession() {
  return Boolean(getAccessToken() || getRefreshToken());
}

export function isAccessTokenExpired(skewMs = 0) {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) {
    return false;
  }
  return Date.now() + skewMs >= expiresAt;
}

export function isAccessTokenExpiringSoon(leadMs = DEFAULT_REFRESH_LEAD_MS) {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) {
    return false;
  }
  return Date.now() + leadMs >= expiresAt;
}

export function getRefreshDelayMs({ leadMs = DEFAULT_REFRESH_LEAD_MS } = {}) {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) {
    return null;
  }
  const remaining = expiresAt - Date.now();
  if (remaining <= leadMs) {
    return MIN_REFRESH_DELAY_MS;
  }
  const delay = Math.min(
    remaining - leadMs,
    Math.max(Math.floor(remaining * 0.8), MIN_REFRESH_DELAY_MS),
  );
  return Math.max(delay, MIN_REFRESH_DELAY_MS);
}

export function setSessionFromLogin({
  access_token: accessToken,
  refresh_token: refreshToken,
  expires_in: expiresIn,
  refresh_expires_in: refreshExpiresIn,
} = {}) {
  if (!accessToken) {
    clearAdminSession();
    return;
  }

  const storage = getStorage();
  writeStorageItem(storage, ACCESS_TOKEN_KEY, accessToken);
  persistExpiry(storage, EXPIRES_AT_KEY, expiresIn);

  if (refreshToken) {
    writeStorageItem(storage, REFRESH_TOKEN_KEY, refreshToken);
    persistExpiry(storage, REFRESH_EXPIRES_AT_KEY, refreshExpiresIn);
  }

  try {
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    // Ignore legacy cleanup failures.
  }
}

export function setAccessToken(token, expiresIn) {
  if (!token) {
    clearAdminSession();
    return;
  }
  setSessionFromLogin({ access_token: token, expires_in: expiresIn });
}

export function clearAdminSession() {
  const storage = getStorage();
  ADMIN_SESSION_KEYS.forEach((key) => removeStorageItem(storage, key));
  try {
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    // Ignore storage access failures during teardown.
  }
}

export function clearAccessToken() {
  clearAdminSession();
}

export function subscribeAdminSessionSync(onRemoteLogout) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = (event) => {
    if (event.storageArea !== localStorage) {
      return;
    }
    if (event.key === ACCESS_TOKEN_KEY && event.oldValue && !event.newValue) {
      onRemoteLogout();
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
